/**
 * QuizBell - Weekly Quiz Notification Button
 *
 * Dark-header quiz bell with:
 * - Brain icon in rounded button
 * - Warm gold glow when quiz is available
 * - Checkmark when completed
 * - Click opens the weekly quiz modal
 */

import { Brain, Check } from 'lucide-react';

interface QuizBellProps {
  isQuizAvailable: boolean;
  hasCompleted: boolean;
  onClick: () => void;
}

export function QuizBell({
  isQuizAvailable,
  hasCompleted,
  onClick,
}: QuizBellProps) {
  const showNotification = isQuizAvailable && !hasCompleted;

  return (
    <button
      onClick={onClick}
      className={`
        ${showNotification
          ? 'bg-white/25 ring-2 ring-brand-secondary/40 shadow-md shadow-brand-secondary/10'
          : hasCompleted
          ? 'bg-white/10 opacity-50'
          : 'bg-white/10 hover:bg-white/20'}
        p-2 rounded-lg transition-all active:scale-95 relative
      `}
      title={
        hasCompleted
          ? "This week's quiz completed! Tap to view results"
          : showNotification
          ? 'New weekly quiz available!'
          : 'Weekly Tech Quiz'
      }
    >
      {hasCompleted ? (
        <div className="relative">
          <Brain className="w-4 h-4 text-white/30" />
          <Check className="w-2.5 h-2.5 text-white/70 absolute -bottom-0.5 -right-0.5" />
        </div>
      ) : (
        <Brain className={`w-4 h-4 transition-colors ${
          showNotification ? 'text-brand-secondary' : 'text-white/70'
        }`} />
      )}
      {showNotification && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-brand-secondary rounded-full border-2 border-brand-dark shadow-sm flex items-center justify-center">
          <span className="text-[9px] font-bold text-white">!</span>
        </span>
      )}
    </button>
  );
}
